import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { locationData, companyBreakeven } from '../data/locations';
import { Edit2, Save, X, Calculator } from 'lucide-react';

const BreakevenReport: React.FC = () => {
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [editableLocations, setEditableLocations] = useState(locationData);
  const [editingLocation, setEditingLocation] = useState<string | null>(null);
  const [tempEdits, setTempEdits] = useState<{
    clients?: number;
    currentRate?: number;
  }>({});
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorInputs, setCalculatorInputs] = useState({
    clients: 0,
    currentRate: 0,
    expenses: 0
  });
  
  const activeLocations = editableLocations.filter(loc => loc.clients > 0);
  
  const getChartData = () => {
    if (selectedLocation === 'all') {
      return activeLocations.map(loc => ({
        name: loc.name,
        current: loc.breakeven.current,
        better: loc.breakeven.better,
        plus5: loc.breakeven.increments["0.05"],
        plus10: loc.breakeven.increments["0.10"],
        plus15: loc.breakeven.increments["0.15"],
        plus20: loc.breakeven.increments["0.20"],
        plus25: loc.breakeven.increments["0.25"]
      }));
    } else {
      const location = editableLocations.find(loc => loc.name === selectedLocation);
      if (!location) return [];
      
      return [
        {
          name: location.name,
          current: location.breakeven.current,
          better: location.breakeven.better,
          plus5: location.breakeven.increments["0.05"],
          plus10: location.breakeven.increments["0.10"],
          plus15: location.breakeven.increments["0.15"],
          plus20: location.breakeven.increments["0.20"],
          plus25: location.breakeven.increments["0.25"]
        }
      ];
    }
  };
  
  const getCompanyData = () => {
    return [
      {
        name: "Company",
        current: companyBreakeven.current,
        plus5: companyBreakeven.increments["0.05"],
        plus10: companyBreakeven.increments["0.10"],
        plus15: companyBreakeven.increments["0.15"],
        plus20: companyBreakeven.increments["0.20"],
        plus25: companyBreakeven.increments["0.25"]
      }
    ];
  };

  // Calculate totals
  const totalClients = activeLocations.reduce((sum, loc) => sum + loc.clients, 0);
  const totalRevenue = activeLocations.reduce((sum, loc) => sum + (loc.clients * loc.breakeven.current), 0);
  const totalBetterRevenue = activeLocations.reduce((sum, loc) => sum + (loc.clients * loc.breakeven.better), 0);
  const totalRevenueGap = totalBetterRevenue - totalRevenue;

  const handleEdit = (locationName: string) => {
    const location = editableLocations.find(loc => loc.name === locationName);
    if (location) {
      setEditingLocation(locationName);
      setTempEdits({
        clients: location.clients,
        currentRate: location.breakeven.current
      });
    }
  };

  const calculateBreakeven = (clients: number, expenses: number) => {
    if (clients <= 0) return 0;
    const baseRate = expenses / clients;
    return {
      current: baseRate,
      better: baseRate * 1.1,
      increments: {
        "0.05": baseRate * 1.05,
        "0.10": baseRate * 1.10,
        "0.15": baseRate * 1.15,
        "0.20": baseRate * 1.20,
        "0.25": baseRate * 1.25
      }
    };
  };

  const handleSave = (locationName: string) => {
    setEditableLocations(locations => 
      locations.map(loc => {
        if (loc.name === locationName && tempEdits.clients && tempEdits.currentRate) {
          // Calculate new breakeven values based on the changes
          const rateChange = tempEdits.currentRate / loc.breakeven.current;
          return {
            ...loc,
            clients: tempEdits.clients,
            breakeven: {
              ...loc.breakeven,
              current: tempEdits.currentRate,
              better: loc.breakeven.better * rateChange,
              increments: {
                "0.05": loc.breakeven.increments["0.05"] * rateChange,
                "0.10": loc.breakeven.increments["0.10"] * rateChange,
                "0.15": loc.breakeven.increments["0.15"] * rateChange,
                "0.20": loc.breakeven.increments["0.20"] * rateChange,
                "0.25": loc.breakeven.increments["0.25"] * rateChange
              }
            }
          };
        }
        return loc;
      })
    );
    setEditingLocation(null);
    setTempEdits({});
  };

  const handleCancel = () => {
    setEditingLocation(null);
    setTempEdits({});
  };

  const handleCalculate = () => {
    const breakeven = calculateBreakeven(calculatorInputs.clients, calculatorInputs.expenses);
    setCalculatorInputs(prev => ({
      ...prev,
      currentRate: breakeven.current
    }));
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Breakeven Analysis</h1>
      
      <div className="mb-6">
        <button
          onClick={() => setShowCalculator(!showCalculator)}
          className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg flex items-center"
        >
          <Calculator className="h-5 w-5 mr-2" />
          {showCalculator ? 'Hide Calculator' : 'Show Calculator'}
        </button>
      </div>

      {showCalculator && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">Breakeven Calculator</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Clients
              </label>
              <input
                type="number"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                value={calculatorInputs.clients}
                onChange={(e) => setCalculatorInputs(prev => ({ ...prev, clients: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Expenses
              </label>
              <input
                type="number"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                value={calculatorInputs.expenses}
                onChange={(e) => setCalculatorInputs(prev => ({ ...prev, expenses: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCalculate}
                className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg"
              >
                Calculate
              </button>
            </div>
          </div>
          {calculatorInputs.currentRate > 0 && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-2">Results:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Breakeven Rate</p>
                  <p className="text-lg font-bold">${calculatorInputs.currentRate.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Better Rate (+10%)</p>
                  <p className="text-lg font-bold">${(calculatorInputs.currentRate * 1.1).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Monthly Revenue</p>
                  <p className="text-lg font-bold">${(calculatorInputs.currentRate * calculatorInputs.clients).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Better Revenue</p>
                  <p className="text-lg font-bold">${(calculatorInputs.currentRate * 1.1 * calculatorInputs.clients).toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-8 overflow-hidden">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Location Breakeven Analysis</h2>
          <div className="mt-4 md:mt-0">
            <select
              className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="all">All Locations</option>
              {activeLocations.map(loc => (
                <option key={loc.name} value={loc.name}>{loc.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Clients</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Current Rate</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Better Rate</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Current Revenue</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Better Revenue</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Revenue Gap</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {activeLocations.map(location => {
                const currentRevenue = location.clients * location.breakeven.current;
                const betterRevenue = location.clients * location.breakeven.better;
                const revenueGap = betterRevenue - currentRevenue;
                const isEditing = editingLocation === location.name;

                return (
                  <tr key={location.name} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{location.name}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input
                          type="number"
                          className="w-24 border rounded px-2 py-1"
                          value={tempEdits.clients}
                          onChange={(e) => setTempEdits({...tempEdits, clients: parseInt(e.target.value)})}
                        />
                      ) : location.clients}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          className="w-24 border rounded px-2 py-1"
                          value={tempEdits.currentRate}
                          onChange={(e) => setTempEdits({...tempEdits, currentRate: parseFloat(e.target.value)})}
                        />
                      ) : `$${location.breakeven.current.toFixed(2)}`}
                    </td>
                    <td className="px-4 py-2">${location.breakeven.better.toFixed(2)}</td>
                    <td className="px-4 py-2">${currentRevenue.toLocaleString()}</td>
                    <td className="px-4 py-2">${betterRevenue.toLocaleString()}</td>
                    <td className="px-4 py-2 text-green-600">+${revenueGap.toLocaleString()}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSave(location.name)}
                            className="text-green-600 hover:text-green-800"
                            title="Save"
                          >
                            <Save className="h-5 w-5" />
                          </button>
                          <button
                            onClick={handleCancel}
                            className="text-red-600 hover:text-red-800"
                            title="Cancel"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(location.name)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-4 py-2">Total</td>
                <td className="px-4 py-2">{totalClients}</td>
                <td className="px-4 py-2">${(totalRevenue / totalClients).toFixed(2)}</td>
                <td className="px-4 py-2">${(totalBetterRevenue / totalClients).toFixed(2)}</td>
                <td className="px-4 py-2">${totalRevenue.toLocaleString()}</td>
                <td className="px-4 py-2">${totalBetterRevenue.toLocaleString()}</td>
                <td className="px-4 py-2 text-green-600">+${totalRevenueGap.toLocaleString()}</td>
                <td className="px-4 py-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-bold mb-6">Company Breakeven Analysis</h2>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={getCompanyData()}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => `$${value}`} />
              <Legend />
              <Bar dataKey="current" name="Current Breakeven" fill="#8884d8" />
              <Bar dataKey="plus5" name="+5% Rate" fill="#ffc658" />
              <Bar dataKey="plus10" name="+10% Rate" fill="#ff8042" />
              <Bar dataKey="plus15" name="+15% Rate" fill="#0088FE" />
              <Bar dataKey="plus20" name="+20% Rate" fill="#00C49F" />
              <Bar dataKey="plus25" name="+25% Rate" fill="#FFBB28" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Breakeven Details</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Location
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Clients
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Current Rate
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Better Rate
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  +5%
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  +10%
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  +15%
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  +20%
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  +25%
                </th>
              </tr>
            </thead>
            <tbody>
              {activeLocations.map((location) => (
                <tr key={location.name}>
                  <td className="py-2 px-4 border-b border-gray-200">{location.name}</td>
                  <td className="py-2 px-4 border-b border-gray-200">{location.clients}</td>
                  <td className="py-2 px-4 border-b border-gray-200">${location.breakeven.current.toFixed(2)}</td>
                  <td className="py-2 px-4 border-b border-gray-200">${location.breakeven.better.toFixed(2)}</td>
                  <td className="py-2 px-4 border-b border-gray-200">${location.breakeven.increments["0.05"].toFixed(2)}</td>
                  <td className="py-2 px-4 border-b border-gray-200">${location.breakeven.increments["0.10"].toFixed(2)}</td>
                  <td className="py-2 px-4 border-b border-gray-200">${location.breakeven.increments["0.15"].toFixed(2)}</td>
                  <td className="py-2 px-4 border-b border-gray-200">${location.breakeven.increments["0.20"].toFixed(2)}</td>
                  <td className="py-2 px-4 border-b border-gray-200">${location.breakeven.increments["0.25"].toFixed(2)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="py-2 px-4 border-b border-gray-200">Company</td>
                <td className="py-2 px-4 border-b border-gray-200">-</td>
                <td className="py-2 px-4 border-b border-gray-200">${companyBreakeven.current.toFixed(2)}</td>
                <td className="py-2 px-4 border-b border-gray-200">-</td>
                <td className="py-2 px-4 border-b border-gray-200">${companyBreakeven.increments["0.05"].toFixed(2)}</td>
                <td className="py-2 px-4 border-b border-gray-200">${companyBreakeven.increments["0.10"].toFixed(2)}</td>
                <td className="py-2 px-4 border-b border-gray-200">${companyBreakeven.increments["0.15"].toFixed(2)}</td>
                <td className="py-2 px-4 border-b border-gray-200">${companyBreakeven.increments["0.20"].toFixed(2)}</td>
                <td className="py-2 px-4 border-b border-gray-200">${companyBreakeven.increments["0.25"].toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BreakevenReport;